import { useApolloClient, useReactiveVar } from "@apollo/client";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { chatToolCallsVar } from "@apps/ask-ai/ask-ai-context";
import AskAiSidebar from "@apps/ask-ai/components/ask-ai-sidebar";

import {
  rolesAndCompetenciesAskAiTarget,
  rolesPageContextMessage,
} from "./role-framework-helpers";
import useRolesAskAiSidebar, {
  resetRoleFrameworkChatStarted,
  roleFrameworkChangeCountVar,
  rolesAskAiSidebarPreferenceKey,
} from "./use-roles-ask-ai-sidebar";

// What the Career Tracks pages read. A change confirmed in the chat
// saves on the server, so these are fetched again to show it.
const roleFrameworkQueryNames = [
  "getOrganizationCareerTracks",
  "getOrganizationCareerTrack",
  "getOrganizationCareerTrackRoles",
  "getOrganizationCompetencies",
  "getOrgWideCompetency",
  "getUnassignedMemberCount",
  "getRoleFrameworkStatus",
  "getPersonCareer",
];

// Refetches the page each time the chat confirms a previewed change. Calls
// that had already finished when the page opened don't count.
const useRefreshAfterConfirmedChanges = () => {
  const client = useApolloClient();
  const toolCalls = useReactiveVar(chatToolCallsVar);
  // Built once, on the first render.
  const [handledCallIds] = useState(
    () => new Set(chatToolCallsVar().map(({ callId }) => callId)),
  );

  useEffect(() => {
    const confirmed = toolCalls.filter(
      ({ callId, toolName, status }) =>
        toolName === "confirm_creation" &&
        status === "completed" &&
        !handledCallIds.has(callId),
    );
    if (confirmed.length === 0) return;
    confirmed.forEach(({ callId }) => handledCallIds.add(callId));
    roleFrameworkChangeCountVar(roleFrameworkChangeCountVar() + 1);
    client.refetchQueries({ include: roleFrameworkQueryNames });
  }, [client, handledCallIds, toolCalls]);
};

// Rendered once by OrgSettings for the Roles and Competencies pages, so a
// chat stays open while moving between the list, a track and a competency.
const RolesAskAiSidebar = () => {
  const { isAvailable } = useRolesAskAiSidebar();
  const location = useLocation();
  useRefreshAfterConfirmedChanges();
  // Unmounting the sidebar, or closing its chat, loses the chat.
  useEffect(() => {
    if (!isAvailable) return;
    return () => {
      resetRoleFrameworkChatStarted();
    };
  }, [isAvailable]);
  if (!isAvailable) return null;
  return (
    <AskAiSidebar
      sidebarExpandedUiPreferenceKey={rolesAskAiSidebarPreferenceKey}
      resetKey="roles-and-competencies"
      askAIEnabled
      ariaLabel="Roles and competencies Ask AI sidebar"
      pendingPromptTarget={rolesAndCompetenciesAskAiTarget}
      onCloseChat={resetRoleFrameworkChatStarted}
      pageContextMessage={rolesPageContextMessage(location.pathname)}
    />
  );
};

export default RolesAskAiSidebar;
